import type React from "react";
import { useEffect, useState } from "react";

import { Modal as SharedModal } from "../ui/modal";
import { Pager, useFitPages } from "../ui/paging";
import { BillingPanel } from "./billing-panel";
import { SubscriptionPanel } from "./subscription-panel";
import { ModulesPanel } from "./modules-panel";
import { PaymentsPanel } from "./payments-panel";
import styles from "./operator-page.module.css";
import {
  clinicDetail,
  clinicPeople,
  grantAdmin,
  reactivateClinic,
  reissueInvite,
  suspendClinic,
  updateClinic,
} from "./operator-api";
import type {
  ClinicAdmin,
  ClinicDetail,
  ClinicNotice,
  ClinicPerson,
  ReissuedInvite,
} from "./operator-api";
import { RequiredMark } from "../ui/required-mark";

/**
 * One clinic — **everything you can do to it, in one place** (STORY-063c).
 *
 * The shape the client chose over a tile grid: a list, a detail page, and modals
 * for the actions. Seven of the console's twelve capabilities are things you do
 * *to a clinic*, and you always arrive already knowing which one — so the object
 * comes first and the verbs hang off it.
 *
 * **There is no Delete button, and its absence is the design** (decision N5).
 * Suspend is the end state; destroying a practice's clinical history is a
 * command run deliberately with the data in hand, not a control sitting beside
 * the one you press while tired.
 */
/**
 * Sentence case, because the console's micro-labels are (CLAUDE.md,
 * *Conventions*) — and **all three states, not two**: `INFO` exists and a
 * ternary on CRITICAL labelled every one of them "Warning", which is a screen
 * telling an operator something the row does not say.
 */
const SEVERITY_LABEL: Record<ClinicNotice["severity"], string> = {
  CRITICAL: "Critical",
  WARNING: "Warning",
  INFO: "For information",
};

/**
 * The clinic's open notices, a page at a time: as many whole rows as the card
 * holds, recounted when the window changes, never a scroll.
 */
function NoticeList({ notices }: { notices: readonly ClinicNotice[] }): React.JSX.Element {
  const { areaRef, measure, page, setPage, pages, range } = useFitPages({
    keys: notices.map((notice) => notice.id),
    estimate: () => 64,
    gap: 0,
  });
  if (notices.length === 0) {
    return <span className={styles.muted}>Nothing open. This clinic is carrying no notices.</span>;
  }
  return (
    <>
      <div className={styles.fitList} ref={areaRef}>
        <ul className={styles.rows} data-testid="notice-list">
          {notices.slice(range[0], range[1]).map((notice) => (
            <li
              key={notice.id}
              ref={measure(notice.id)}
              className={styles.row}
              data-severity={notice.severity}
            >
              <div className={styles.rowMain}>
                <span className={styles.rowName}>{notice.title}</span>
                <span className={styles.rowSub}>
                  {notice.detail ?? notice.kind}
                  {notice.branchName === null ? "" : ` · ${notice.branchName}`}
                </span>
              </div>
              <span className={styles.noticeSeverity} data-severity={notice.severity}>
                {SEVERITY_LABEL[notice.severity]}
              </span>
            </li>
          ))}
        </ul>
      </div>
      <Pager
        page={page}
        count={pages.length}
        onPage={setPage}
        from={range[0] + 1}
        to={range[1]}
        total={notices.length}
        noun="notices"
        testId="notice-pager"
      />
    </>
  );
}

/** The clinic's parts, in the order an operator reaches for them. */
const CLINIC_TABS = [
  "Overview",
  "Modules",
  "Subscription",
  "Billing",
  "Payments",
  "Admins",
  "Notices",
] as const;
type ClinicTab = (typeof CLINIC_TABS)[number];

export function ClinicDetailPage({
  clinicId,
  onBack,
}: {
  clinicId: string;
  onBack: () => void;
}): React.JSX.Element {
  const [clinic, setClinic] = useState<ClinicDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<"reissue" | "edit" | "suspend" | null>(null);
  const [reissuing, setReissuing] = useState<ClinicAdmin | null>(null);
  const [reissued, setReissued] = useState<ReissuedInvite | null>(null);
  const [promoting, setPromoting] = useState(false);
  /** Which part of the clinic is open (Emile, 2026-10-17: one card and tabs, like a patient's file). */
  const [tab, setTab] = useState<ClinicTab>("Overview");

  const load = (): void => {
    void clinicDetail(clinicId)
      .then((next) => {
        setClinic(next);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not load this clinic");
      });
  };

  useEffect(load, [clinicId]);

  if (error !== null && clinic === null) {
    return (
      <div className={styles.console}>
        <button type="button" className={styles.ghost} onClick={onBack}>
          ← Clinics
        </button>
        <p className={styles.error} role="alert">
          {error}
        </p>
      </div>
    );
  }

  if (clinic === null) return <p className={styles.muted}>Loading…</p>;

  return (
    <div className={`${styles.console} ${styles.clinicPage}`}>
      <button type="button" className={styles.back} onClick={onBack}>
        ← Clinics
      </button>

      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>{clinic.name}</h1>
          <p className={styles.muted}>
            {clinic.slug} · created {new Date(clinic.createdAt).toLocaleDateString()}
            {clinic.createdBy === null ? "" : ` by ${clinic.createdBy}`}
          </p>
        </div>
        <ClinicStatus clinic={clinic} />
      </header>

      {error !== null && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      {clinic.isSuspended && (
        <section className={styles.suspendedBanner} data-testid="suspension-notice">
          <strong>Suspended</strong>
          <p>
            {clinic.suspendedReason}
            {clinic.suspendedBy === null ? "" : ` — ${clinic.suspendedBy}`}
            {clinic.suspendedAt === null
              ? ""
              : `, ${new Date(clinic.suspendedAt).toLocaleDateString()}`}
          </p>
          <p className={styles.muted}>
            Nobody at this clinic can sign in. Every record is untouched and returns the moment this
            is lifted.
          </p>
        </section>
      )}

      <div className={styles.actions}>
        <button
          type="button"
          className={styles.primary}
          onClick={() => {
            const pending = clinic.admins.find((a) => a.state !== "ACTIVE") ?? clinic.admins[0];
            if (pending !== undefined) {
              setReissuing(pending);
              setAction("reissue");
            }
          }}
          disabled={clinic.admins.length === 0}
          data-testid="reissue-invite"
        >
          Reissue invite
        </button>
        <button
          type="button"
          className={styles.ghost}
          onClick={() => {
            setAction("edit");
          }}
          data-testid="edit-clinic"
        >
          Edit details
        </button>
        {clinic.isSuspended ? (
          <button
            type="button"
            className={styles.ghost}
            data-testid="reactivate-clinic"
            onClick={() => {
              void reactivateClinic(clinic.id)
                .then(setClinic)
                .catch((cause: unknown) => {
                  setError(cause instanceof Error ? cause.message : "Could not reactivate");
                });
            }}
          >
            Reactivate
          </button>
        ) : (
          <button
            type="button"
            className={styles.danger}
            disabled={clinic.isDemo}
            title={clinic.isDemo ? "The demo clinic cannot be suspended" : undefined}
            onClick={() => {
              setAction("suspend");
            }}
            data-testid="suspend-clinic"
          >
            Suspend
          </button>
        )}
      </div>

      {reissued !== null && (
        <InviteHandover
          email={reissued.email}
          link={reissued.inviteUrl}
          expiresAt={reissued.inviteExpiresAt}
          emailed={reissued.emailed}
          slug={clinic.slug}
          onDone={() => {
            setReissued(null);
          }}
        />
      )}

      {/*
        **One card, and the clinic's parts as tabs** (Emile, 2026-10-17: *"let's
        have one table and tabs, just like the patients page"*). It was six
        panels stacked down a page that scrolled 1,400-1,700px; now the card
        fills the window and one part is open at a time. Every panel and every
        button it had is still here, under its tab.
      */}
      <section className={styles.tabCard} data-testid="clinic-tabs">
        <nav className={styles.clinicTabs}>
          {CLINIC_TABS.map((name) => {
            const count =
              name === "Admins"
                ? clinic.admins.length
                : name === "Notices" && clinic.notices.length > 0
                  ? clinic.notices.length
                  : null;
            return (
              <button
                key={name}
                type="button"
                className={
                  name === tab ? `${styles.clinicTab} ${styles.clinicTabOn}` : styles.clinicTab
                }
                aria-current={name === tab ? "page" : undefined}
                onClick={() => setTab(name)}
                data-testid={`clinic-tab-${name.toLowerCase()}`}
              >
                {name}
                {count !== null && (
                  <span
                    className={name === "Notices" ? styles.clinicTabWarm : styles.clinicTabCount}
                  >
                    {name === "Notices" ? `${String(count)} open` : count}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className={styles.tabBody} data-testid="clinic-tab-body">
          {tab === "Overview" && (
            <>
              <div className={styles.tabSection}>
                <span className={styles.panelTitle}>Setup progress</span>
                <div className={styles.steps}>
                  <Step done={clinic.branchCount > 0} label="Location" />
                  <Step done={clinic.canTakeBookings} label="Chair" />
                  <Step done={clinic.canTakeBookings} label="Provider" />
                  <Step done={clinic.canTakeBookings} label="Procedure" />
                </div>
                <span className={styles.muted}>
                  {clinic.canTakeBookings
                    ? "This clinic can take bookings."
                    : clinic.branchCount === 0
                      ? "Nothing started — they may not have signed in yet."
                      : "Started, but not yet able to take a booking."}
                </span>
              </div>

              <div className={styles.tabSection}>
                <span className={styles.panelTitle}>Details</span>
                <dl className={styles.kv}>
                  <div>
                    <dt>Timezone</dt>
                    <dd>{clinic.timezone}</dd>
                  </div>
                  <div>
                    <dt>Currency</dt>
                    <dd>{clinic.currency}</dd>
                  </div>
                  <div>
                    <dt>Country</dt>
                    <dd>{clinic.defaultCountry ?? "—"}</dd>
                  </div>
                  <div>
                    <dt>Locations</dt>
                    <dd>{clinic.branchCount}</dd>
                  </div>
                  <div>
                    <dt>People</dt>
                    <dd>{clinic.userCount}</dd>
                  </div>
                  <div>
                    <dt>Patients</dt>
                    <dd>{clinic.patientCount}</dd>
                  </div>
                </dl>
              </div>
            </>
          )}

          {tab === "Notices" && (
            <div className={styles.tabSection}>
              {/*
                **What this clinic is currently carrying** (G17, STORY-050c).

                The same `clinic_alerts` rows the practice sees on Home, shown here so
                the platform side can spot a practice whose Google calendar and
                DentaCore keep disagreeing — without reading logs, and before anybody
                rings up about it.

                **Nothing is actionable from here.** Resolving a notice is the
                practice's own judgement about their own schedule; an operator marking
                it done would be deciding on their behalf about a patient they cannot
                see. This is a window, and it says so by having no buttons.

                Its own tab since 2026-10-17, paged to the card: ten notices ran the
                Overview 400px past a laptop's window.
              */}
              <span className={styles.panelTitle}>Open notices</span>
              <NoticeList notices={clinic.notices} />
            </div>
          )}

          {tab === "Modules" && <ModulesPanel clinic={clinic} onChanged={setClinic} />}

          {tab === "Subscription" && <SubscriptionPanel clinicId={clinic.id} />}

          {/* Mounted only while open; it reads the clinic's prices when it opens. */}
          {tab === "Billing" && (
            <BillingPanel
              clinicId={clinic.id}
              modulesKey={clinic.modules.map((m) => `${m.module}${String(m.enabled)}`).join()}
            />
          )}

          {tab === "Payments" && <PaymentsPanel clinicId={clinic.id} />}

          {tab === "Admins" && (
            <div className={styles.tabSection}>
              <div className={styles.panelHead}>
                <span className={styles.panelTitle}>Admins</span>
                {/*
                **The second way out of a locked-out practice** (QA20-11). The
                first is the clinic's own last-admin guard, which refuses to let the
                only administrator remove themselves — this is what is left when
                that guard was never reached, and until now the answer was an
                operator with database access and a hand-written UPDATE.
              */}
                <button
                  type="button"
                  className={styles.ghost}
                  onClick={() => setPromoting(true)}
                  data-testid="admin-promote-open"
                >
                  Make someone an admin
                </button>
              </div>
              {clinic.admins.length === 0 ? (
                <span className={styles.warn} data-testid="admin-none">
                  <strong>No admins — nobody can reach this clinic&rsquo;s settings.</strong> Make
                  one below.
                </span>
              ) : (
                <ul className={styles.rows} data-testid="admin-list">
                  {clinic.admins.map((admin) => (
                    <li key={admin.id} className={styles.row}>
                      <div className={styles.rowMain}>
                        <span className={styles.rowName}>{admin.name}</span>
                        <span className={styles.rowSub}>{admin.email}</span>
                      </div>
                      <AdminBadge admin={admin} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </section>

      {promoting && (
        <PromoteModal
          clinicId={clinic.id}
          onClose={() => setPromoting(false)}
          onPromoted={() => {
            setPromoting(false);
            load();
          }}
        />
      )}

      {action === "reissue" && reissuing !== null && (
        <ReissueModal
          admin={reissuing}
          onCancel={() => {
            setAction(null);
          }}
          onDone={(result) => {
            setAction(null);
            setReissued(result);
            load();
          }}
          clinicId={clinic.id}
        />
      )}

      {action === "edit" && (
        <EditModal
          clinic={clinic}
          onCancel={() => {
            setAction(null);
          }}
          onSaved={(next) => {
            setAction(null);
            setClinic(next);
          }}
        />
      )}

      {action === "suspend" && (
        <SuspendModal
          clinic={clinic}
          onCancel={() => {
            setAction(null);
          }}
          onSuspended={(next) => {
            setAction(null);
            setClinic(next);
          }}
        />
      )}
    </div>
  );
}

const Step = ({ done, label }: { done: boolean; label: string }): React.JSX.Element => (
  <span className={done ? styles.stepDone : styles.stepTodo}>{label}</span>
);

/**
 * The status chip, and the reason the list screen works.
 *
 * Every support question — *are they stuck, did they ever start, why are they
 * locked out* — is answered by scanning one column. **"Invite not accepted" is
 * the one acted on most**, and it is why the chip distinguishes a clinic that
 * never began from one that began and stopped.
 */
export function ClinicStatus({
  clinic,
}: {
  clinic: {
    isSuspended: boolean;
    canTakeBookings: boolean;
    userCount: number;
    branchCount: number;
  };
}): React.JSX.Element {
  if (clinic.isSuspended) {
    return (
      <span className={styles.chipOff} data-testid="clinic-readiness">
        Suspended
      </span>
    );
  }
  if (clinic.canTakeBookings) {
    return (
      <span className={styles.chipOk} data-testid="clinic-readiness">
        Taking bookings
      </span>
    );
  }
  return (
    <span className={styles.chipWarn} data-testid="clinic-readiness">
      {clinic.branchCount === 0 ? "Not started" : "Setup unfinished"}
    </span>
  );
}

const AdminBadge = ({ admin }: { admin: ClinicAdmin }): React.JSX.Element => {
  if (admin.state === "ACTIVE") return <span className={styles.chipOk}>Active</span>;

  const when =
    admin.inviteExpiresAt === null
      ? ""
      : ` · ${admin.state === "EXPIRED" ? "expired" : "expires"} ${new Date(
          admin.inviteExpiresAt,
        ).toLocaleDateString()}`;

  return (
    <span className={admin.state === "EXPIRED" ? styles.chipOff : styles.chipWarn}>
      {admin.state === "EXPIRED" ? "Invite expired" : "Invited"}
      {when}
    </span>
  );
};

/** The link, shown once — only its hash is stored, so nothing can show it again. */
function InviteHandover({
  email,
  link,
  expiresAt,
  emailed,
  slug,
  onDone,
}: {
  email: string;
  /**
   * The link, from the server, at the clinic's own subdomain.
   *
   * **A whole URL rather than a token to assemble here** (QA20-5). This used to
   * take the token and build `` `${window.location.origin}/accept/…` ``, and an
   * operator is always at the apex — so every link this panel ever displayed
   * pointed at a host with no tenant to resolve, and the token was refused.
   */
  link: string;
  expiresAt: string;
  emailed: boolean;
  slug: string;
  onDone: () => void;
}): React.JSX.Element {
  const [copied, setCopied] = useState(false);

  return (
    <section
      className={emailed ? styles.handover : styles.handoverWarn}
      data-testid="invite-handover"
    >
      <h2 className={styles.h2}>{emailed ? "Invite sent" : "Invite created, but not sent"}</h2>
      <p>
        {emailed ? (
          <>
            Emailed to <strong>{email}</strong>. They set their own password — nothing travels from
            here.
          </>
        ) : (
          <>
            <strong>The email did not go out.</strong> The invite is valid; send this link to{" "}
            <strong>{email}</strong> yourself, or try again once mail is working.
          </>
        )}
      </p>

      <div className={styles.linkRow}>
        <code className={styles.link} data-testid="invite-link">
          {link}
        </code>
        <button
          type="button"
          className={styles.ghost}
          onClick={() => {
            void navigator.clipboard?.writeText(link).then(() => {
              setCopied(true);
            });
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>

      <p className={styles.muted}>
        They sign in at <strong>{slug}</strong>. The link expires{" "}
        {new Date(expiresAt).toLocaleDateString()}, works once, and any previous one is now dead.
      </p>

      <button type="button" className={styles.primary} onClick={onDone}>
        Done
      </button>
    </section>
  );
}

function ReissueModal({
  admin,
  clinicId,
  onCancel,
  onDone,
}: {
  admin: ClinicAdmin;
  clinicId: string;
  onCancel: () => void;
  onDone: (result: ReissuedInvite) => void;
}): React.JSX.Element {
  const [email, setEmail] = useState(admin.email);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <Modal label="Reissue the invite" onCancel={onCancel}>
      <form
        className={styles.dialogBody}
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          void reissueInvite(clinicId, admin.id, email === admin.email ? undefined : email)
            .then(onDone)
            .catch((cause: unknown) => {
              setError(cause instanceof Error ? cause.message : "Could not reissue");
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        <h2 className={styles.h2}>Reissue the invite</h2>
        <p className={styles.muted}>
          Sends a fresh link and <strong>kills the old one immediately</strong>. Correct the address
          here if it was wrong the first time.
        </p>

        <label className={styles.field}>
          <span>Send to</span>
          <input
            type="email"
            value={email}
            required
            data-testid="reissue-email"
            onChange={(e) => {
              setEmail(e.target.value);
            }}
          />
        </label>

        <p className={styles.muted}>
          The link lasts 7 days and works once. They choose their own password.
        </p>

        {error !== null && (
          <p className={styles.error} role="alert" data-testid="reissue-error">
            {error}
          </p>
        )}

        <div className={styles.dialogActions}>
          <button type="button" className={styles.ghost} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.primary}
            disabled={busy}
            data-testid="reissue-send"
          >
            {busy ? "Sending…" : "Send new invite"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditModal({
  clinic,
  onCancel,
  onSaved,
}: {
  clinic: ClinicDetail;
  onCancel: () => void;
  onSaved: (next: ClinicDetail) => void;
}): React.JSX.Element {
  const [name, setName] = useState(clinic.name);
  const [timezone, setTimezone] = useState(clinic.timezone);
  const [currency, setCurrency] = useState(clinic.currency);
  const [country, setCountry] = useState(clinic.defaultCountry ?? "");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <Modal label="Edit clinic details" onCancel={onCancel}>
      <form
        className={styles.dialogBody}
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          void updateClinic(clinic.id, { name, timezone, currency, defaultCountry: country })
            .then(onSaved)
            .catch((cause: unknown) => {
              setError(cause instanceof Error ? cause.message : "Could not save");
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        <h2 className={styles.h2}>Edit details</h2>
        <p className={styles.muted}>
          The subdomain is not editable — it is how they reach DentaCore, and changing it would
          break every link they have.
        </p>

        <label className={styles.field}>
          <span>
            Clinic name
            <RequiredMark />
          </span>
          <input
            value={name}
            required
            data-testid="edit-name"
            onChange={(e) => {
              setName(e.target.value);
            }}
          />
        </label>

        <div className={styles.pair}>
          <label className={styles.field}>
            <span>
              Timezone
              <RequiredMark />
            </span>
            <input
              value={timezone}
              required
              onChange={(e) => {
                setTimezone(e.target.value);
              }}
            />
          </label>
          <label className={styles.field}>
            <span>
              Currency
              <RequiredMark />
            </span>
            <input
              value={currency}
              required
              maxLength={3}
              onChange={(e) => {
                setCurrency(e.target.value);
              }}
            />
            <small className={styles.hint}>Refused once they have priced anything.</small>
          </label>
        </div>

        <label className={styles.field}>
          <span>Country</span>
          <input
            value={country}
            maxLength={2}
            placeholder="LB"
            onChange={(e) => {
              setCountry(e.target.value);
            }}
          />
          <small className={styles.hint}>Leave empty if they serve more than one.</small>
        </label>

        {error !== null && (
          <p className={styles.error} role="alert" data-testid="edit-error">
            {error}
          </p>
        )}

        <div className={styles.dialogActions}>
          <button type="button" className={styles.ghost} onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className={styles.primary} disabled={busy} data-testid="edit-save">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function SuspendModal({
  clinic,
  onCancel,
  onSuspended,
}: {
  clinic: ClinicDetail;
  onCancel: () => void;
  onSuspended: (next: ClinicDetail) => void;
}): React.JSX.Element {
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <Modal label="Suspend this clinic" onCancel={onCancel}>
      <form
        className={styles.dialogBody}
        onSubmit={(event) => {
          event.preventDefault();
          setBusy(true);
          setError(null);
          void suspendClinic(clinic.id, reason)
            .then(onSuspended)
            .catch((cause: unknown) => {
              setError(cause instanceof Error ? cause.message : "Could not suspend");
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        <h2 className={styles.h2}>Suspend {clinic.name}</h2>
        <p className={styles.muted}>
          <strong>Nobody there will be able to sign in.</strong> Every record stays exactly where it
          is, and reactivating restores access completely — so this is reversible, and nothing is
          deleted.
        </p>
        <p className={styles.muted}>
          They are not emailed. Tell them yourself, so a mistake here is recoverable.
        </p>

        <label className={styles.field}>
          <span>Why</span>
          <input
            value={reason}
            required
            minLength={3}
            placeholder="Non-payment — third reminder sent 12 Aug"
            data-testid="suspend-reason"
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
          <small className={styles.hint}>
            Stored with your name and the date. This is what answers &ldquo;why is this clinic
            off?&rdquo; in three months.
          </small>
        </label>

        {error !== null && (
          <p className={styles.error} role="alert" data-testid="suspend-error">
            {error}
          </p>
        )}

        <div className={styles.dialogActions}>
          <button type="button" className={styles.ghost} onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className={styles.danger}
            disabled={busy}
            data-testid="suspend-confirm"
          >
            {busy ? "Suspending…" : "Suspend"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * The console's own overlay, now a thin adapter over the shared `Modal`.
 *
 * This file got there first and grew an Escape listener of its own; what it
 * never had was **dismissal by clicking the backdrop**, which is what the
 * client asked for on every popup in the product (items 23 and 30). Keeping the
 * local name means the eight call sites below are untouched, while the
 * behaviour underneath becomes the same one every other dialog has.
 */
function Modal({
  label,
  onCancel,
  children,
}: {
  label: string;
  onCancel: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <SharedModal label={label} onClose={onCancel} panelClassName={styles.dialog}>
      {children}
    </SharedModal>
  );
}

/**
 * Hand the keys to somebody who already works here (QA20-11).
 *
 * ── Why this is a list and not an address field ─────────────────────────────
 *
 * Typing an address would let an operator create an admin out of nothing, which
 * is a different and much larger capability: O1 keeps platform admins outside
 * tenancy, and a console that can add people to a live tenant is a hole in that
 * wall rather than a door through it. Picking from people the clinic already
 * has means the worst an operator can do is promote the wrong colleague, which
 * the clinic's own admin can undo.
 *
 * **Somebody who cannot sign in is shown and disabled, not hidden.** An invite
 * nobody accepted is the single likeliest reason a practice is locked out, and
 * hiding that person would leave the operator staring at a list that does not
 * explain itself. The row says what to do instead.
 */
function PromoteModal({
  clinicId,
  onClose,
  onPromoted,
}: {
  clinicId: string;
  onClose: () => void;
  onPromoted: () => void;
}): React.JSX.Element {
  const [people, setPeople] = useState<ClinicPerson[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    void clinicPeople(clinicId)
      .then(setPeople)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not read this clinic's people");
      });
  }, [clinicId]);

  const promote = (person: ClinicPerson): void => {
    setBusy(person.id);
    setError(null);
    void grantAdmin(clinicId, person.id)
      .then(onPromoted)
      .catch((cause: unknown) => {
        setError(cause instanceof Error ? cause.message : "Could not make them an admin");
        setBusy(null);
      });
  };

  return (
    <Modal label="Make someone an admin" onCancel={onClose}>
      <h2 className={styles.h2}>Make someone an admin</h2>
      <p className={styles.muted}>
        They will be able to reach System — locations, chairs, prices, people and billing setup.
        Nothing else changes, and the clinic&rsquo;s own admins can undo it.
      </p>

      {error !== null && (
        <p className={styles.error} role="alert" data-testid="promote-error">
          {error}
        </p>
      )}

      {people === null ? (
        <p className={styles.muted}>Reading&hellip;</p>
      ) : people.length === 0 ? (
        <p className={styles.muted}>Nobody works at this clinic yet.</p>
      ) : (
        <ul className={styles.rows} data-testid="promote-list">
          {people.map((person) => (
            <li key={person.id} className={styles.row}>
              <div className={styles.rowMain}>
                <span className={styles.rowName}>{person.name}</span>
                <span className={styles.rowSub}>
                  {person.email} · {person.role.toLowerCase().replace(/_/g, " ")}
                </span>
              </div>
              {person.canAdminister ? (
                <span className={styles.ready}>Already an admin</span>
              ) : !person.isActive ? (
                // Named rather than hidden — see the note above this component.
                <span className={styles.notReady}>Has not signed in yet</span>
              ) : (
                <button
                  type="button"
                  className={styles.primary}
                  disabled={busy !== null}
                  onClick={() => promote(person)}
                  data-testid={`promote-${person.id}`}
                >
                  {busy === person.id ? "Making…" : "Make admin"}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      <div className={styles.actions}>
        <button type="button" className={styles.ghost} onClick={onClose}>
          Done
        </button>
      </div>
    </Modal>
  );
}
